import { useState, useRef, type ReactNode } from 'react';
import { LayoutGrid, Save, X } from 'lucide-react';
import { CollapsibleSection } from './CollapsibleSection';

export interface SectionDef {
  /** Unique stable key for this section */
  id: string;
  /** Display title shown in the section header */
  title: string;
  /** Section body — rendered inside CollapsibleSection */
  content: ReactNode;
  /** Whether the section is open by default when no persisted state exists */
  defaultOpen?: boolean;
}

interface PageSectionsLayoutProps {
  /** Identifier for the page, e.g. "venue-detail" or "event-detail" */
  pageKey: string;
  /** Current user ID — used to namespace per-user layout preferences */
  userId: string;
  sections: SectionDef[];
}

function orderStorageKey(pageKey: string, userId: string) {
  return `sections:${pageKey}:${userId}:order`;
}

function loadOrder(pageKey: string, userId: string, defaults: string[]): string[] {
  try {
    const raw = localStorage.getItem(orderStorageKey(pageKey, userId));
    if (!raw) return defaults;
    const parsed: string[] = JSON.parse(raw);
    // Keep only ids that still exist, then append any new ids at the end
    const existing = parsed.filter((id) => defaults.includes(id));
    const added = defaults.filter((id) => !existing.includes(id));
    return [...existing, ...added];
  } catch {
    return defaults;
  }
}

function saveOrder(pageKey: string, userId: string, order: string[]) {
  try {
    localStorage.setItem(orderStorageKey(pageKey, userId), JSON.stringify(order));
  } catch {
    // ignore
  }
}

export function PageSectionsLayout({ pageKey, userId, sections }: PageSectionsLayoutProps) {
  const defaultOrder = sections.map((s) => s.id);
  const storageKey = `${pageKey}:${userId}`;

  const [order, setOrder] = useState<string[]>(() => loadOrder(pageKey, userId, defaultOrder));
  const [isDesignMode, setIsDesignMode] = useState(false);
  const [draftOrder, setDraftOrder] = useState<string[]>([]);

  // Drag state
  const dragId = useRef<string | null>(null);
  const dragOverId = useRef<string | null>(null);

  const sectionMap = Object.fromEntries(sections.map((s) => [s.id, s]));

  // ── Design mode ────────────────────────────────────────────────

  function enterDesignMode() {
    setDraftOrder([...order]);
    setIsDesignMode(true);
  }

  function exitDesignMode() {
    setIsDesignMode(false);
    setDraftOrder([]);
    dragId.current = null;
    dragOverId.current = null;
  }

  function saveLayout() {
    setOrder(draftOrder);
    saveOrder(pageKey, userId, draftOrder);
    exitDesignMode();
  }

  // ── Drag-and-drop handlers ──────────────────────────────────────

  function handleDragStart(id: string) {
    dragId.current = id;
  }

  function handleDragOver(e: React.DragEvent, id: string) {
    e.preventDefault();
    dragOverId.current = id;

    if (dragId.current && dragId.current !== id) {
      setDraftOrder((prev) => {
        const fromIdx = prev.indexOf(dragId.current!);
        const toIdx = prev.indexOf(id);
        if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return prev;
        const next = [...prev];
        next.splice(fromIdx, 1);
        next.splice(toIdx, 0, dragId.current!);
        return next;
      });
    }
  }

  function handleDragEnd() {
    dragId.current = null;
    dragOverId.current = null;
  }

  // ── Render ──────────────────────────────────────────────────────

  const activeOrder = isDesignMode ? draftOrder : order;

  return (
    <div>
      {/* Toolbar */}
      <div className="flex justify-end mb-4">
        {isDesignMode ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 mr-1">Drag sections to reorder</span>
            <button
              onClick={saveLayout}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Save size={13} />
              Save Layout
            </button>
            <button
              onClick={exitDesignMode}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <X size={13} />
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={enterDesignMode}
            title="Customize section layout"
            className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-200 text-gray-500 text-xs rounded-lg hover:bg-gray-50 hover:text-gray-700 transition-colors"
          >
            <LayoutGrid size={13} />
            Customize Layout
          </button>
        )}
      </div>

      {/* Sections */}
      <div className="space-y-6">
        {activeOrder.map((id) => {
          const section = sectionMap[id];
          if (!section) return null;
          return (
            <CollapsibleSection
              key={id}
              id={id}
              storageKey={storageKey}
              title={section.title}
              defaultOpen={section.defaultOpen ?? true}
              isDragMode={isDesignMode}
              dragProps={
                isDesignMode
                  ? {
                      draggable: true,
                      onDragStart: (e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        handleDragStart(id);
                      },
                      onDragOver: (e) => handleDragOver(e, id),
                      onDrop: (e) => {
                        e.preventDefault();
                      },
                      onDragEnd: handleDragEnd,
                    }
                  : undefined
              }
            >
              {section.content}
            </CollapsibleSection>
          );
        })}
      </div>
    </div>
  );
}
